import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
    ArrowDown,
    ArrowUpDown,
    ArrowUpRight,
    Baby,
    BellRing,
    BookOpen,
    Briefcase,
    Car,
    ChevronDown,
    Cross,
    DoorOpen,
    Droplets,
    Dumbbell,
    FileDown,
    Film,
    Flame,
    Flower2,
    Landmark,
    LayoutGrid,
    Lock,
    MapPin,
    MoveHorizontal,
    Phone,
    ShieldCheck,
    Sparkles,
    Store,
    Trees,
    Video,
    Warehouse,
    Waves,
    Wine,
    Zap,
} from "lucide-react";
import { Reveal, MaskedLines, EASE } from "@/components/motion";
import WhatsAppCTA, { ContactNotice } from "@/components/WhatsAppCTA";
import FloorPlan from "./FloorPlan";
import MasterPlan from "./MasterPlan";
import { VERIFY_NOTE, waLink } from "@/lib/site";

const ICONS = {
    Waves, Dumbbell, Sparkles, Wine, Film, BookOpen, Trees, BellRing,
    ShieldCheck, Car, Zap, MapPin, Video, Droplets, Flame, ArrowUpDown,
    Baby, Store, Warehouse, Landmark, DoorOpen, Lock, LayoutGrid,
    MoveHorizontal, Flower2, Briefcase, Cross,
};

function SectionShell({ overline, title, children, note, id }) {
    return (
        <section id={id} className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24">
            <Reveal>
                {overline && <p className="eyebrow">{overline}</p>}
                {title && (
                    <h2 className="mt-4 max-w-3xl font-serif text-2xl leading-snug text-ivory sm:text-3xl lg:text-4xl">
                        {title}
                    </h2>
                )}
            </Reveal>
            <div className="mt-10">{children}</div>
            {note && <p className="mt-6 text-[11px] text-dim">{note}</p>}
        </section>
    );
}

function Overview({ s }) {
    return (
        <SectionShell overline={s.overline} title={s.title}>
            <div className="grid gap-12 lg:grid-cols-12">
                <div className="lg:col-span-7">
                    {s.paragraphs.map((p, i) => (
                        <Reveal key={i} delay={i * 0.1}>
                            <p className={`leading-relaxed text-mist ${i === 0 ? "text-lg" : "mt-5 text-base"}`}>
                                {p}
                            </p>
                        </Reveal>
                    ))}
                </div>
                <div className="lg:col-span-5">
                    {s.facts ? (
                        <div
                            className="overflow-hidden rounded-2xl border border-black/8 bg-card"
                            data-testid="overview-facts"
                        >
                            {s.facts.map((f, i) => (
                                <div
                                    key={i}
                                    className="flex items-start justify-between gap-6 border-b border-black/5 px-6 py-4 last:border-0"
                                >
                                    <span className="shrink-0 pt-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-dim">
                                        {f.label}
                                    </span>
                                    <span className="text-right text-sm text-ivory">{f.value}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-black/8 bg-black/5">
                            {(s.stats || []).map((st, i) => (
                                <div key={i} className="bg-card p-6 sm:p-8">
                                    <p className="font-serif text-4xl text-gold">{st.value}</p>
                                    <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-dim">
                                        {st.label}
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </SectionShell>
    );
}

function Highlights({ s }) {
    return (
        <SectionShell overline={s.overline} title={s.title}>
            <div className="grid gap-px overflow-hidden rounded-2xl border border-black/8 bg-black/5 sm:grid-cols-2 lg:grid-cols-3">
                {s.items.map((item, i) => (
                    <div key={i} className="group bg-card p-7 transition-colors duration-300 hover:bg-secondary">
                        <p className="font-mono text-xs text-gold">
                            {String(i + 1).padStart(2, "0")}
                        </p>
                        <h3 className="mt-4 font-serif text-xl text-ivory">{item.title}</h3>
                        <p className="mt-2 text-sm leading-relaxed text-mist">{item.text}</p>
                    </div>
                ))}
            </div>
        </SectionShell>
    );
}

const SPANS = {
    4: "lg:col-span-4",
    6: "lg:col-span-6",
    8: "lg:col-span-8",
    12: "lg:col-span-12",
};

function Gallery({ s }) {
    return (
        <SectionShell overline={s.overline} title={s.title} note={s.caption}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-12">
                {s.images.map((img, i) => (
                    <Reveal
                        key={i}
                        delay={(i % 3) * 0.08}
                        className={SPANS[img.span] || SPANS[4]}
                    >
                        <a
                            href={img.src}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group relative block h-64 overflow-hidden rounded-2xl sm:h-72"
                            data-testid={`gallery-item-${i}`}
                        >
                            <img
                                src={img.src}
                                alt={img.caption}
                                loading="lazy"
                                className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                            <span className="absolute bottom-4 left-4 translate-y-2 font-mono text-[10px] uppercase tracking-[0.25em] text-ivory opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                                {img.caption}
                            </span>
                        </a>
                    </Reveal>
                ))}
            </div>
        </SectionShell>
    );
}

function Amenities({ s }) {
    return (
        <SectionShell overline={s.overline} title={s.title} note={s.note}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {s.items.map((a, i) => {
                    const Icon = ICONS[a.icon] || Sparkles;
                    return (
                        <Reveal key={i} delay={(i % 4) * 0.06}>
                            <div className="group h-full rounded-2xl border border-black/8 bg-card p-6 transition-colors duration-300 hover:border-gold/40">
                                <Icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
                                <h3 className="mt-4 font-serif text-lg text-ivory">{a.name}</h3>
                                <p className="mt-1.5 text-sm text-mist">{a.desc}</p>
                            </div>
                        </Reveal>
                    );
                })}
            </div>
        </SectionShell>
    );
}

function Plans({ s }) {
    const [active, setActive] = useState(0);
    const plan = s.items[active];
    if (!plan) return null;
    return (
        <SectionShell overline={s.overline} title={s.title} note={s.note}>
            {s.items.length > 1 && (
                <div className="flex flex-wrap gap-2" data-testid="plan-tabs">
                    {s.items.map((p, i) => (
                        <button
                            key={p.label}
                            type="button"
                            data-testid={`plan-tab-${i}`}
                            onClick={() => setActive(i)}
                            className={`rounded-full border px-5 py-2.5 text-sm transition-all duration-300 ${
                                i === active
                                    ? "border-gold bg-gold text-white"
                                    : "border-black/10 text-mist hover:border-gold/40 hover:text-ivory"
                            }`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
            )}
            <motion.div
                key={active}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="mt-8"
            >
                <a
                    href={plan.image}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="plan-open-full"
                    className="group block overflow-hidden rounded-2xl border border-black/10 bg-white p-3 transition-colors hover:border-gold/40"
                >
                    <img
                        src={plan.image}
                        alt={`${plan.label} plan`}
                        loading="lazy"
                        className="mx-auto max-h-[70vh] w-auto max-w-full rounded-lg"
                    />
                    <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-dim transition-colors group-hover:text-gold">
                        {plan.label} · Tap to view full size
                    </p>
                </a>
            </motion.div>
        </SectionShell>
    );
}

function Configurations({ s }) {
    const [activeId, setActiveId] = useState(s.units[0]?.id);
    const unit = s.units.find((u) => u.id === activeId) || s.units[0];
    return (
        <SectionShell overline={s.overline} title={s.title} note={s.note}>
            <div className="flex flex-wrap gap-2" data-testid="config-tabs">
                {s.units.map((u) => (
                    <button
                        key={u.id}
                        type="button"
                        data-testid={`config-tab-${u.id}`}
                        onClick={() => setActiveId(u.id)}
                        className={`rounded-full border px-5 py-2.5 text-sm transition-all duration-300 ${
                            u.id === unit.id
                                ? "border-gold bg-gold text-white"
                                : "border-black/10 text-mist hover:border-gold/40 hover:text-ivory"
                        }`}
                    >
                        {u.name}
                    </button>
                ))}
            </div>
            {unit && (
                <motion.div
                    key={unit.id}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: EASE }}
                    className="mt-8 grid gap-8 lg:grid-cols-12"
                    data-testid="config-panel"
                >
                    <div className="lg:col-span-7">
                        <FloorPlan planId={unit.planId} title={unit.name} />
                    </div>
                    <div className="lg:col-span-5">
                        <h3 className="font-serif text-2xl text-ivory">{unit.name}</h3>
                        <p className="mt-1 font-mono text-xs uppercase tracking-[0.2em] text-gold">
                            {unit.area}
                        </p>
                        <p className="mt-4 text-sm leading-relaxed text-mist">{unit.desc}</p>
                        <ul className="mt-6 space-y-3">
                            {unit.features.map((f) => (
                                <li key={f} className="flex items-start gap-3 text-sm text-mist">
                                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gold" />
                                    {f}
                                </li>
                            ))}
                        </ul>
                    </div>
                </motion.div>
            )}
        </SectionShell>
    );
}

function Specs({ s }) {
    return (
        <SectionShell overline={s.overline} title={s.title} note={s.note}>
            <div className="grid gap-px overflow-hidden rounded-2xl border border-black/8 bg-black/5 md:grid-cols-2">
                {s.groups.map((g, i) => (
                    <div key={i} className="bg-card p-7">
                        <h3 className="font-serif text-xl text-gold">{g.title}</h3>
                        <ul className="mt-4 space-y-3">
                            {g.items.map((item) => (
                                <li key={item} className="flex items-start gap-3 text-sm text-mist">
                                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gold/70" />
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        </SectionShell>
    );
}

function Developer({ s }) {
    return (
        <SectionShell overline={s.overline} title={s.title} note={s.note}>
            <div className="grid gap-10 lg:grid-cols-12">
                <div className="lg:col-span-7">
                    {s.fictional && (
                        <p className="mb-5 inline-block rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                            Fictional concept developer
                        </p>
                    )}
                    <p className="font-serif text-xl italic text-mist">{s.tagline}</p>
                    {s.story.map((p, i) => (
                        <p key={i} className="mt-5 leading-relaxed text-mist">
                            {p}
                        </p>
                    ))}
                </div>
                <div className="lg:col-span-5">
                    <div className="rounded-2xl border border-black/8 bg-card p-7">
                        <p className="eyebrow">Portfolio</p>
                        <ul className="mt-5 space-y-4">
                            {s.portfolio.map((p) => {
                                const item = typeof p === "string" ? { name: p } : p;
                                return (
                                    <li
                                        key={item.name}
                                        className="flex items-center justify-between gap-4 border-b border-black/5 pb-4 text-sm text-mist last:border-0 last:pb-0"
                                    >
                                        {item.name}
                                        {item.tag && (
                                            <span className="shrink-0 font-mono text-[10px] uppercase tracking-widest text-dim">
                                                {item.tag}
                                            </span>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </div>
            </div>
        </SectionShell>
    );
}

function Location({ s }) {
    return (
        <SectionShell overline={s.overline} title={s.title} note={s.note}>
            <div className="grid gap-8 lg:grid-cols-12">
                {s.image && (
                    <Reveal className="lg:col-span-7">
                        <div className="group relative overflow-hidden rounded-2xl border border-black/8">
                            <img
                                src={s.image}
                                alt="Location keyplan"
                                loading="lazy"
                                className="max-h-96 w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.02]"
                            />
                        </div>
                    </Reveal>
                )}
                <div className={s.image ? "lg:col-span-5" : "lg:col-span-12 max-w-2xl"}>
                    {s.addressLines && (
                        <div className="mb-6 rounded-2xl border border-black/8 bg-card p-6">
                            <p className="eyebrow">Address</p>
                            <p className="mt-3 text-sm leading-relaxed text-ivory">
                                {s.addressLines.map((line, i) => (
                                    <span key={i} className="block">
                                        {line}
                                    </span>
                                ))}
                            </p>
                            {s.mapUrl && (
                                <a
                                    href={s.mapUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="location-map-link"
                                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-gold transition-colors hover:text-gold-bright"
                                >
                                    Open in Google Maps
                                    <ArrowUpRight className="h-4 w-4" />
                                </a>
                            )}
                        </div>
                    )}
                    {s.intro && <p className="leading-relaxed text-mist">{s.intro}</p>}
                    <ul className="mt-6 space-y-4">
                        {s.landmarks.map((l) => (
                            <li
                                key={l.name}
                                className="flex items-center justify-between gap-4 border-b border-black/5 pb-4"
                            >
                                <span className="flex items-center gap-2.5 text-sm text-ivory">
                                    <MapPin className="h-4 w-4 shrink-0 text-gold" />
                                    {l.name}
                                </span>
                                <span className="shrink-0 font-mono text-[10px] uppercase tracking-widest text-dim">
                                    {l.detail}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </SectionShell>
    );
}

function Faqs({ s }) {
    const [open, setOpen] = useState(0);
    return (
        <SectionShell overline={s.overline} title={s.title}>
            <div className="mx-auto max-w-3xl divide-y divide-black/8 rounded-2xl border border-black/8 bg-card px-6 sm:px-8" data-testid="faq-list">
                {s.items.map((f, i) => (
                    <div key={i}>
                        <button
                            type="button"
                            data-testid={`faq-toggle-${i}`}
                            onClick={() => setOpen(open === i ? -1 : i)}
                            className="flex w-full items-center justify-between gap-6 py-6 text-left"
                        >
                            <span className="font-serif text-lg text-ivory">{f.q}</span>
                            <ChevronDown
                                className={`h-5 w-5 shrink-0 text-gold transition-transform duration-300 ${
                                    open === i ? "rotate-180" : ""
                                }`}
                            />
                        </button>
                        <motion.div
                            initial={false}
                            animate={{
                                height: open === i ? "auto" : 0,
                                opacity: open === i ? 1 : 0,
                            }}
                            transition={{ duration: 0.4, ease: EASE }}
                            className="overflow-hidden"
                        >
                            <p className="pb-6 text-sm leading-relaxed text-mist">{f.a}</p>
                        </motion.div>
                    </div>
                ))}
            </div>
        </SectionShell>
    );
}

function CtaSection({ s, project }) {
    return (
        <section className="border-t border-black/5 bg-surface/60">
            <div className="mx-auto max-w-7xl px-5 py-20 text-center sm:px-8 sm:py-28">
                <Reveal>
                    <p className="eyebrow">{s.overline}</p>
                    <h2 className="mx-auto mt-4 max-w-2xl font-serif text-3xl leading-tight text-ivory sm:text-4xl lg:text-5xl">
                        {s.title}
                    </h2>
                    <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-mist">
                        {s.text}
                    </p>
                    <div className="mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row">
                        <WhatsAppCTA
                            phone={project.whatsapp}
                            projectName={project.name}
                            label="WhatsApp Developer"
                            testid="project-whatsapp-cta"
                            large
                        />
                        {project.phone && (
                            <a
                                href={`tel:${project.phone}`}
                                data-testid="project-call-cta"
                                className="inline-flex items-center justify-center gap-2.5 rounded-full border border-gold/60 px-8 py-4 text-base font-medium text-gold transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold/10"
                            >
                                <Phone className="h-5 w-5" />
                                Call {project.phoneDisplay || "Developer"}
                            </a>
                        )}
                    </div>
                    {s.brochureUrl ? (
                        <a
                            href={s.brochureUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="brochure-download-cta"
                            className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-mist transition-colors hover:text-gold"
                        >
                            <FileDown className="h-4 w-4" />
                            Download project brochure (PDF)
                        </a>
                    ) : (
                        s.showBrochure && (
                            <a
                                href={waLink(
                                    project.whatsapp,
                                    project.name,
                                    `Hi, I found ${project.name} on Sthaan and would like to receive the project brochure.`,
                                )}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="brochure-request-cta"
                                className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-mist transition-colors hover:text-gold"
                            >
                                <FileDown className="h-4 w-4" />
                                Request Brochure
                            </a>
                        )
                    )}
                    <div className="mt-6 flex justify-center">
                        <ContactNotice />
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

const RENDERERS = {
    overview: Overview,
    highlights: Highlights,
    gallery: Gallery,
    amenities: Amenities,
    plans: Plans,
    configurations: Configurations,
    masterplan: ({ s }) => (
        <SectionShell overline={s.overline} title={s.title}>
            <MasterPlan hotspots={s.hotspots} note={s.note} />
        </SectionShell>
    ),
    specs: Specs,
    developer: Developer,
    location: Location,
    faqs: Faqs,
    cta: CtaSection,
    text: ({ s }) => (
        <SectionShell overline={s.overline} title={s.title}>
            {(s.paragraphs || []).map((p, i) => (
                <p key={i} className="mt-4 max-w-3xl leading-relaxed text-mist">
                    {p}
                </p>
            ))}
        </SectionShell>
    ),
};

export default function ProjectShowcase({ project }) {
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: heroRef,
        offset: ["start start", "end start"],
    });
    const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

    return (
        <article data-testid="project-showcase">
            <div ref={heroRef} className="relative flex min-h-[92vh] items-end overflow-hidden">
                <motion.div style={{ y: heroY }} className="absolute inset-0 scale-110">
                    <img
                        src={project.hero.image}
                        alt={project.name}
                        className="h-full w-full object-cover"
                    />
                </motion.div>
                <div className="absolute inset-0 bg-obsidian/25" />
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(100deg, rgba(250,248,244,0.97) 0%, rgba(250,248,244,0.82) 45%, rgba(250,248,244,0.3) 78%)",
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-transparent" />
                {project.demo && (
                    <div className="absolute left-1/2 top-24 -translate-x-1/2 sm:top-28">
                        <p
                            data-testid="demo-badge"
                            className="glass gold-ring rounded-full px-5 py-2 font-mono text-[10px] uppercase tracking-[0.3em] text-gold"
                        >
                            Demo · Concept Project
                        </p>
                    </div>
                )}
                <div className="relative mx-auto w-full max-w-7xl px-5 pb-20 sm:px-8">
                    <p className="eyebrow">{project.hero.overline}</p>
                    <h1 className="mt-5 font-serif text-4xl font-light leading-[1.05] tracking-tight text-ivory sm:text-6xl lg:text-7xl">
                        <MaskedLines lines={project.hero.lines} />
                    </h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9, duration: 0.8, ease: EASE }}
                        className="mt-6 max-w-xl text-sm leading-relaxed text-mist sm:text-base"
                    >
                        {project.hero.sub}
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.1, duration: 0.8, ease: EASE }}
                        className="mt-9 flex flex-wrap items-center gap-3"
                    >
                        {[project.status, project.typeLabel, project.locality, project.city]
                            .filter(Boolean)
                            .map((chip) => (
                                <span
                                    key={chip}
                                    className="glass rounded-full px-4 py-2 text-xs text-ivory"
                                >
                                    {chip}
                                </span>
                            ))}
                    </motion.div>
                </div>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.6, duration: 1 }}
                    className="absolute bottom-6 left-1/2 -translate-x-1/2"
                >
                    <ArrowDown className="h-5 w-5 animate-bounce text-gold/70" />
                </motion.div>
            </div>

            {project.sections.map((s, i) => {
                const Renderer = RENDERERS[s.type];
                if (!Renderer) return null;
                return <Renderer key={`${s.type}-${i}`} s={s} project={project} />;
            })}

            <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
                <div className="rounded-2xl border border-black/5 bg-surface/60 p-6">
                    <p className="text-[11px] leading-relaxed text-dim" data-testid="project-verify-note">
                        {VERIFY_NOTE}
                    </p>
                </div>
            </section>

            <div className="fixed inset-x-0 bottom-0 z-40 border-t border-black/10 bg-obsidian/90 p-3 backdrop-blur-xl sm:hidden">
                <div className="flex gap-2">
                    <WhatsAppCTA
                        phone={project.whatsapp}
                        projectName={project.name}
                        label="WhatsApp Developer"
                        testid="project-sticky-whatsapp"
                        className="flex-1"
                    />
                    {project.phone && (
                        <a
                            href={`tel:${project.phone}`}
                            data-testid="project-sticky-call"
                            aria-label="Call developer"
                            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-gold/60 text-gold"
                        >
                            <Phone className="h-4 w-4" />
                        </a>
                    )}
                </div>
            </div>
        </article>
    );
}
